/**
 * Guide Data Adapters
 *
 * Utility functions to transform guide profile data between different formats
 * Ensures compatibility between API responses and component expectations
 *
 * Epic: MVP 1.2 - Guide Profiles & Listings
 * MVP 1.27.7-S (SCRUM-787) — Asset-aware photo URL resolution
 * Location: src/utils/guideDataAdapters.js
 */

import { getAssetUrl, buildSrcSet } from '../services/assetService';

/**
 * Adapt guide profile data for GuideCard component
 * Transforms API guide profile response to GuideCard component format
 *
 * @param {Object} profile - Guide profile from API
 * @returns {Object} GuideCard-compatible object
 */
export function adaptProfileForGuideCard(profile) {
  if (!profile) {return null;}

  return {
    id: profile.guide_id || profile.id,
    name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.business_name || 'Guide',
    bio: profile.service_description || profile.bio || '',
    avatar: getGuidePhotoUrl(profile, 'profile', 'thumbnail') || '/api/placeholder/120/120',
    stats: {
      reviews: profile.total_reviews || profile.reviewCount || 0,
      rating: parseFloat(profile.avg_rating || profile.averageRating || 0),
      trips: profile.total_trips || profile.tripCount || 0
    }
  };
}

/**
 * Adapt array of guide profiles for GuideCard list
 *
 * @param {Array} profiles - Array of guide profiles from API
 * @returns {Array} Array of GuideCard-compatible objects
 */
export function adaptProfilesForGuideCardList(profiles) {
  if (!Array.isArray(profiles)) {return [];}
  return profiles.map(adaptProfileForGuideCard).filter(Boolean);
}

/**
 * Adapt guide profile data for profile page display
 * Ensures all expected fields exist with defaults
 *
 * @param {Object} profile - Guide profile from API
 * @returns {Object} Normalized profile object
 */
export function normalizeGuideProfile(profile) {
  if (!profile) {return null;}

  return {
    guide_id: profile.guide_id || profile.id,
    first_name: profile.first_name || '',
    last_name: profile.last_name || '',
    business_name: profile.business_name || null,
    slug: profile.slug || null,
    profile_photo_url: profile.profile_photo_url || null,
    cover_photo_url: profile.cover_photo_url || null,
    bio: profile.bio || '',
    service_description: profile.service_description || '',
    primary_activity: profile.primary_activity || '',
    day_rate_cents: profile.day_rate_cents || null,
    experience_range: profile.experience_range || '',
    availability_estimate: profile.availability_estimate || '',
    service_radius_miles: profile.service_radius_miles || null,
    base_location_lat: profile.base_location_lat || null,
    base_location_lng: profile.base_location_lng || null,

    // Stats
    total_trips: profile.total_trips || 0,
    total_reviews: profile.total_reviews || 0,
    avg_rating: parseFloat(profile.avg_rating || 0).toFixed(2),

    // Social proof
    current_viewers: profile.current_viewers || 0,
    bookings_last_30_days: profile.bookings_last_30_days || 0,
    has_limited_availability: profile.has_limited_availability || false,

    // Metadata
    profile_completion_status: profile.profile_completion_status || 'incomplete',
    approval_status: profile.approval_status || 'pending',
    created_at: profile.created_at,
    updated_at: profile.updated_at
  };
}

/**
 * Format day rate for display
 *
 * @param {number} cents - Day rate in cents
 * @returns {string} Formatted price (e.g., "$250")
 */
export function formatDayRate(cents) {
  if (!cents || cents <= 0) {return 'Contact for pricing';}
  return `$${(cents / 100).toFixed(0)}`;
}

/**
 * Format day rate range for display
 *
 * @param {number} minCents - Minimum day rate in cents
 * @param {number} maxCents - Maximum day rate in cents
 * @returns {string} Formatted price range (e.g., "$250-$500")
 */
export function formatDayRateRange(minCents, maxCents) {
  if (!minCents && !maxCents) {return 'Contact for pricing';}
  if (!maxCents || minCents === maxCents) {return formatDayRate(minCents);}

  const min = (minCents / 100).toFixed(0);
  const max = (maxCents / 100).toFixed(0);
  return `$${min}-$${max}`;
}

/**
 * Get activity display name
 *
 * @param {string} activity - Activity slug
 * @returns {string} Formatted activity name
 */
export function getActivityDisplayName(activity) {
  if (!activity) {return '';}

  // Convert snake_case or kebab-case to Title Case
  return activity
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Calculate profile completion percentage
 * Used for progress indicators during signup
 *
 * @param {Object} profile - Guide profile data
 * @returns {number} Completion percentage (0-100)
 */
export function calculateProfileCompleteness(profile) {
  if (!profile) {return 0;}

  const requiredFields = [
    'first_name',
    'last_name',
    'profile_photo_url',
    'service_description',
    'primary_activity',
    'day_rate_cents',
    'service_radius_miles'
  ];

  const optionalFields = [
    'bio',
    'cover_photo_url',
    'business_name',
    'experience_range',
    'availability_estimate'
  ];

  const requiredCount = requiredFields.filter(field => profile[field]).length;
  const optionalCount = optionalFields.filter(field => profile[field]).length;

  // Required fields are worth 80%, optional fields 20%
  const requiredPercentage = (requiredCount / requiredFields.length) * 80;
  const optionalPercentage = (optionalCount / optionalFields.length) * 20;

  return Math.round(requiredPercentage + optionalPercentage);
}

/**
 * Generate profile URL from slug or ID
 *
 * @param {string|null} slug - Profile slug
 * @param {string} id - Profile ID (UUID)
 * @returns {string} Profile URL
 */
export function getProfileUrl(slug, id) {
  if (slug) {
    return `/guides/${slug}`;
  }
  if (id) {
    return `/guides/${id}`;
  }
  return '/guides';
}

/**
 * Get guide photo URL from StoredAsset (preferred) or legacy URL column.
 *
 * SCRUM-787: Frontend consumers should call this instead of reading
 * profile_photo_url / cover_photo_url directly. When the API returns
 * a profilePhoto or coverPhoto StoredAsset object, the best variant
 * for the requested size is selected. Falls back to the legacy URL
 * column when no asset is available.
 *
 * @param {Object} profile - Guide profile object from API
 * @param {'profile'|'cover'} purpose - Which photo to resolve
 * @param {string} [preferredSize] - Variant size (default: 'medium' for profile, 'large' for cover)
 * @returns {string|null} CDN URL or null
 */
export function getGuidePhotoUrl(profile, purpose, preferredSize) {
  if (!profile) {
    return null;
  }

  if (purpose === 'profile') {
    const size = preferredSize || 'medium';
    const assetUrl = profile.profilePhoto ? getAssetUrl(profile.profilePhoto, size) : null;
    return assetUrl || profile.profile_photo_url || null;
  }

  if (purpose === 'cover') {
    const size = preferredSize || 'large';
    const assetUrl = profile.coverPhoto ? getAssetUrl(profile.coverPhoto, size) : null;
    return assetUrl || profile.cover_photo_url || null;
  }

  return null;
}

/**
 * Build srcSet string for a guide photo from StoredAsset variants.
 *
 * MVP 1.27.7-S (SCRUM-793) — Responsive Image srcSet
 *
 * @param {Object} profile - Guide profile object from API
 * @param {'profile'|'cover'} purpose - Which photo to resolve
 * @returns {string|null} srcSet string or null if no variants available
 */
export function getGuidePhotoSrcSet(profile, purpose) {
  if (!profile) {
    return null;
  }

  if (purpose === 'profile' && profile.profilePhoto) {
    return buildSrcSet(profile.profilePhoto);
  }

  if (purpose === 'cover' && profile.coverPhoto) {
    return buildSrcSet(profile.coverPhoto);
  }

  return null;
}
